export interface IMeterResponse {
  billingAccountNumber: string;
  isDeleted: boolean;
  meterId: number;
  meterNumber: string;
  meterUid: string;
  partnerId: number | null;
  propertyId: number;
  serviceAccountNumber: string;
  siteId: string;
  systemId: number | null;
  userId: string;
  utilityId: number;
}

// Meters list + classification (task-4062, spec ZEOS-MTR-MULTI v0.3). Values mirror MeteringVocabulary.cs.

export const METER_ROLE = {
  Primary: "PRIMARY",
  Sub: "SUB",
  Excluded: "EXCLUDED",
} as const;

export const METER_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: METER_ROLE.Primary, label: "Counts toward the building" },
  { value: METER_ROLE.Sub, label: "Sub-meter (inside another meter)" },
  { value: METER_ROLE.Excluded, label: "Excluded" },
];

export const METER_OCCUPANCY_OPTIONS: { value: string; label: string }[] = [
  { value: "UNKNOWN", label: "Unknown" },
  { value: "OCCUPIED", label: "Occupied" },
  { value: "VACANT", label: "Vacant" },
];

/** Reference__MeterClass ids the rules key on; they never change. */
export const METER_CLASS_ID = {
  Unassigned: 0,
  Other: 1,
  Generation: 15,
} as const;

/** The meters list groups by class from this many live meters (below it, one flat list). */
export const METER_GROUPING_THRESHOLD = 6;

export interface IMeterClassOption {
  meterClassId: number;
  code: string;
  name: string;
  sortOrder: number;
}

export interface IPropertyMeterContract {
  meterId: number;
  propertyId: number;
  meterUid?: string | null;
  meterNumber?: string | null;
  /** SAID for UtilityAPI meters. */
  serviceAccountNumber?: string | null;
  billingAccountNumber?: string | null;
  serviceAddress?: string | null;
  utilityTypeId?: number | null;
  serviceCode?: string | null;
  serviceName?: string | null;
  partnerId?: number | null;
  source: string;
  utilityName?: string | null;
  /** Link Utility / property-create row naming the utility, not a physical meter (OI-05). The API leaves out the ones a connected meter supersedes. */
  isSetupRecord?: boolean;
  meterClassId: number;
  meterClassCode?: string | null;
  meterClassName?: string | null;
  displayLabel?: string | null;
  rollupRole: string;
  parentMeterId?: number | null;
  occupancyState: string;
  connectionStatus: string;
  activeFrom?: string | null;
  activeTo?: string | null;
  isRetired: boolean;
  lastReportedDate?: string | null;
  statusNote?: string | null;
}

export interface IPropertyMetersContract {
  propertyId: number;
  propertyName: string;
  isSingleMeterInvisible: boolean;
  meters: IPropertyMeterContract[];
  meterClasses: IMeterClassOption[];
}

/** Coverage ledger, one service (task-4051 P2-03). Mirrors ServiceCoverageResult. */
export interface IServiceCoverage {
  utilityTypeId: number;
  serviceTypeCode?: string | null;
  serviceTypeName?: string | null;
  meteringProfile?: string | null;
  expectedMeterCount?: number | null;
  linkedMeterCount: number;
  reportingMeterCount: number;
  stalledMeterCount: number;
  pendingMeterCount: number;
  subMeterCount: number;
  excludedMeterCount: number;
  missingMeterCount: number;
  /** COMPLETE / PARTIAL / ACTION_NEEDED / UNKNOWN; null when the service isn't used here (NOT_PRESENT). */
  coverageState?: string | null;
  /** MISSING / STALLED when ACTION_NEEDED. */
  actionNeededReason?: string | null;
  isAwaitingAttestation: boolean;
  exceedsExpected: boolean;
}

/** Coverage ledger for a property: weakest service state, the service responsible, and each service. */
export interface IPropertyCoverage {
  propertyId: number;
  coverageState?: string | null;
  responsibleServiceTypeCode?: string | null;
  isSingleMeterInvisible: boolean;
  services: IServiceCoverage[];
}

export interface IMeterClassificationRequest {
  meterClassId: number;
  displayLabel?: string | null;
  rollupRole: string;
  parentMeterId?: number | null;
  occupancyState?: string | null;
  /** Null keeps the current note. */
  statusNote?: string | null;
}
